<template>
  <div class="home-page">
    <header class="topbar">
      <button class="logo" type="button" @click="$router.replace('/Home')">
        {{ brandName }}
      </button>

      <div class="top-actions">
        <div class="menu" :class="{ open: showLangMenu }">
          <button class="pill" type="button" @click.stop="toggleLangMenu">
            <span class="pill-lab">{{ currentLanguage.label }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9l6 6 6-6"></path>
            </svg>
          </button>

          <div v-if="showLangMenu" class="menu-pop lang-pop">
            <button
              v-for="item in languageOptions"
              :key="item.code"
              type="button"
              :class="{ active: displayLang === item.code }"
              @click.stop="changeLang(item)"
            >
              <span>{{ item.short }}</span>
              <b>{{ item.fullLabel }}</b>
            </button>
          </div>
        </div>
      </div>
    </header>

    <main class="page-body">
      <section v-if="heroItems.length" class="hero">
        <div class="hero-track" :style="heroTrackStyle">
          <div
            v-for="item in heroItems"
            :key="`hero-${item.id}`"
            class="hero-slide"
            @click="goProfile(item)"
          >
            <img :src="item.image" :alt="item.name" />
            <div class="hero-mask"></div>
            <div class="hero-book">
              <span class="dot"></span>
              {{ item.tag }}
            </div>

            <div class="hero-content">
              <div class="hero-kicker">
                <span class="dot"></span>
                {{ copy.heroKicker }}
              </div>
              <div class="hero-name">
                <h2>{{ item.name }}</h2>
                <p>{{ item.role }} · {{ item.city }}</p>
              </div>
              <span class="hero-cta">
                {{ copy.heroAction }}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                  <path d="M9 18l6-6-6-6"></path>
                </svg>
              </span>
            </div>
          </div>
        </div>

        <div class="hero-dots">
          <i
            v-for="(item, index) in heroItems"
            :key="`dot-${item.id}`"
            :class="{ on: heroIndex === index }"
            @click="setHeroIndex(index)"
          ></i>
        </div>
      </section>

      <div class="entries">
        <button
          v-for="entry in homeEntries"
          :key="entry.key"
          class="entry"
          type="button"
          @click="handleEntry(entry)"
        >
          <div class="entry-ico" :style="entry.style">
            <svg v-if="entry.key === 'dating'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z"></path>
            </svg>
            <svg v-else-if="entry.key === 'vip'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 3l7 4v5c0 5-3.5 7.74-7 9-3.5-1.26-7-4-7-9V7l7-4z"></path>
              <path d="M9 12l2 2 4-4"></path>
            </svg>
            <svg v-else-if="entry.key === 'live'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="5" width="18" height="14" rx="3"></rect>
              <path d="M10 9l5 3-5 3V9z"></path>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="9"></circle>
              <path d="M12 7v10"></path>
              <path d="M7 12h10"></path>
            </svg>
          </div>
          <span>{{ entry.label }}</span>
        </button>
      </div>

      <section class="sec">
        <div class="sec-h">
          <div>
            <h3>
              {{ copy.crushTitle }}
              <span class="badge">{{ onlineCountText }}</span>
            </h3>
          </div>
          <button class="link" type="button" @click="openViewMore">
            {{ copy.viewMore || copy.viewAll }}
          </button>
        </div>

        <div class="crush">
          <button
            v-for="item in crushItems"
            :key="`crush-${item.id}`"
            class="crush-item"
            type="button"
            @click="goProfile(item)"
          >
            <div class="crush-av">
              <img :src="item.image" :alt="item.name" />
              <i class="st" :class="{ busy: !item.online }"></i>
            </div>
            <b>{{ item.name }}</b>
          </button>
        </div>
      </section>

      <section ref="recommendSection" class="sec">
        <div class="sec-h">
          <div>
            <h3>{{ copy.recommendTitle }}</h3>
          </div>
        </div>

        <div class="tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="tab"
            :class="{ on: activeTab === tab.key }"
            type="button"
            @click="switchTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="grid">
          <button
            v-for="item in cardsToShow"
            :key="`card-${item.id}`"
            class="card"
            type="button"
            @click="goProfile(item)"
          >
            <div class="cover">
              <img :src="item.image" :alt="item.name" />
              <div class="card-tag">
                <span class="dot" :class="{ pink: !item.online }"></span>
                {{ item.tag }}
              </div>
              <div v-if="item.certified" class="card-ok">√ {{ certifiedLabel }}</div>
              <div class="card-topline">
                <span>{{ item.name }}</span>
                <span
                  class="card-heart"
                  :class="{ on: favoriteIds.includes(Number(item.id)) }"
                  @click.stop="toggleFavorite(item)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z"></path>
                  </svg>
                </span>
              </div>
            </div>

            <div class="card-info">
              <div class="card-data">
                <span>{{ copy.identity }}</span>
                <b>{{ item.role }}</b>
              </div>
              <div class="card-data">
                <span>{{ copy.city }}</span>
                <b>{{ item.city }}</b>
              </div>
              <div class="card-data">
                <span>{{ copy.profileId }}</span>
                <b>{{ item.publicId || "--" }}</b>
              </div>
              <div class="card-data service">
                <span>{{ copy.service }}</span>
                <b>{{ item.about }}</b>
              </div>
            </div>
          </button>
        </div>

        <button
          v-if="hasMoreCards"
          type="button"
          class="home-load-more"
          :disabled="listLoading"
          @click="loadMoreCards"
        >
          {{ listLoading ? (copy.loadingText || "加载中…") : (copy.loadMore || "显示更多") }}
        </button>
      </section>
    </main>

  </div>
</template>

<script>
import { getListPayload } from "@/common/apiData";
import { getPageCopy } from "@/common/pageLocale";
import {
  DEFAULT_LANGUAGE_OPTION,
  LANGUAGE_OPTIONS,
  applyAppLanguage,
  findLanguageOption,
  readSavedLanguageOption,
} from "@/common/languages";
import { openKefuContact, resolveLanguageOptions } from "@/common/kefu";
import { resolveAssetUrl } from "@/common/runtimeConfig";
import { buildAddressMap, buildAddressTabs, normalizeXuanfeiProfile } from "@/common/xuanfeiProfile";

const PAGE_SIZE = 12;

export default {
  data() {
    return {
      displayLang: "zh_cn",
      languageOptions: LANGUAGE_OPTIONS,
      showLangMenu: false,
      profiles: [],
      addressMap: {},
      areaTabs: [],
      notice: "",
      heroIndex: 0,
      heroTimer: null,
      activeTab: "recommend",
      favoriteIds: [],
      listPage: 1,
      listHasMore: false,
      listLoading: false,
      displayLimit: PAGE_SIZE,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "home");
    },
    baseInfo() {
      return this.$store.getters.getBaseInfo || {};
    },
    ui() {
      return this.baseInfo.ui || {};
    },
    brandName() {
      return String(this.ui.brand || this.baseInfo.name || "OMN")
        .replace(/MShub/gi, "OMN")
        .trim() || "OMN";
    },
    certifiedLabel() {
      const raw = this.ui.certified_label;
      const text =
        raw != null && String(raw).trim()
          ? String(raw).trim()
          : this.copy.certified || "已认证";
      return text.replace(/已完善资料/g, "已认证").replace(/Profile completed/gi, "Verified");
    },
    onlineCountText() {
      const raw = this.ui.online_count_cap_text;
      if (raw != null && String(raw).trim()) {
        return String(raw).trim();
      }
      return this.copy.onlineCountCap || "99+";
    },
    currentLanguage() {
      return findLanguageOption(this.displayLang) || this.languageOptions[0] || DEFAULT_LANGUAGE_OPTION;
    },
    heroItems() {
      return this.profiles.slice(0, 5);
    },
    heroTrackStyle() {
      return {
        transform: `translateX(-${this.heroIndex * 100}%)`,
      };
    },
    homeEntries() {
      return [
        {
          key: "dating",
          label: this.copy.dating,
          style: {
            "--entry-color": "#ff76a5",
            "--entry-glow": "rgba(255, 63, 120, 0.18)",
          },
        },
        {
          key: "vip",
          label: this.copy.vip,
          style: {
            "--entry-color": "#c69aff",
            "--entry-glow": "rgba(165, 92, 255, 0.18)",
          },
        },
        {
          key: "live",
          label: this.copy.live,
          style: {
            "--entry-color": "#f2c36d",
            "--entry-glow": "rgba(255, 198, 92, 0.16)",
          },
        },
        {
          key: "newcomer",
          label: this.copy.newcomer,
          style: {
            "--entry-color": "#8fd7ff",
            "--entry-glow": "rgba(90, 190, 255, 0.18)",
          },
        },
      ];
    },
    crushItems() {
      return this.profiles.slice(0, 12);
    },
    tabs() {
      // 附近地区：以后台 address 接口返回为准
      if (this.areaTabs.length) {
        return this.areaTabs;
      }
      return [
        { key: "recommend", label: this.copy.tabs.recommend },
        { key: "nearby", label: this.copy.tabs.nearby },
        { key: "online", label: this.copy.tabs.online },
        { key: "newcomer", label: this.copy.tabs.newcomer },
      ];
    },
    filteredProfiles() {
      let list = this.profiles;
      if (String(this.activeTab).indexOf("area-") === 0) {
        const areaId = Number(String(this.activeTab).replace("area-", ""));
        list = this.profiles.filter((item) => Number(item.classId) === areaId);
      } else if (this.activeTab === "recommend") {
        const preferred = this.profiles.filter(
          (item) => item.hot || Number(item.raw && item.raw.is_recommend) === 1
        );
        list = preferred.length ? preferred : this.profiles;
      } else if (this.activeTab === "nearby") {
        const nearby = this.profiles.filter((item) => item.group === "nearby");
        list = nearby.length ? nearby : this.profiles;
      } else if (this.activeTab === "online") {
        const online = this.profiles.filter((item) => item.online);
        list = online.length ? online : this.profiles;
      } else if (this.activeTab === "newcomer") {
        const newcomers = this.profiles.filter((item) => item.isNew);
        list = newcomers.length ? newcomers : this.profiles;
      }
      return list.length ? list : this.profiles;
    },
    cardsToShow() {
      // 每次只展示一截，点「显示更多」再加 PAGE_SIZE 条
      return this.filteredProfiles.slice(0, this.displayLimit);
    },
    hasMoreCards() {
      return this.displayLimit < this.filteredProfiles.length || this.listHasMore;
    },
  },
  watch: {
    baseInfo: {
      deep: true,
      handler(info) {
        this.languageOptions = resolveLanguageOptions(info, LANGUAGE_OPTIONS);
      },
    },
  },
  created() {
    this.languageOptions = resolveLanguageOptions(this.baseInfo, LANGUAGE_OPTIONS);
    this.initLanguage();
    this.fetchHomeData();
  },
  mounted() {
    window.addEventListener("click", this.handleWindowClick);
  },
  beforeDestroy() {
    window.removeEventListener("click", this.handleWindowClick);
    this.stopHeroAutoplay();
  },
  methods: {
    initLanguage() {
      this.applyLanguage(readSavedLanguageOption(), false);
    },
    applyLanguage(language, closeMenu = true) {
      const next = applyAppLanguage(this, language);
      this.displayLang = next.code;
      if (closeMenu) {
        this.showLangMenu = false;
      }
    },
    changeLang(language) {
      this.applyLanguage(language);
      this.fetchHomeData();
    },
    toggleLangMenu() {
      this.showLangMenu = !this.showLangMenu;
    },
    handleWindowClick() {
      this.showLangMenu = false;
    },
    normalizeProfile(item, index) {
      return normalizeXuanfeiProfile(item, index, {
        copy: this.copy,
        addressMap: this.addressMap,
        lang: this.$i18n.locale || this.displayLang || "zh_cn",
        resolveAssetUrl,
      });
    },
    async fetchAddressMap() {
      try {
        const res = await this.$http({
          method: "get",
          url: "address_list",
        });
        const list = Array.isArray(res && res.data) ? res.data : getListPayload(res && res.data);
        const lang = this.$i18n.locale || this.displayLang || "zh_cn";
        this.addressMap = buildAddressMap(list, lang);
        this.areaTabs = buildAddressTabs(list, lang);
      } catch (error) {
        this.addressMap = {};
        this.areaTabs = [];
      }
    },
    resolveDefaultTab() {
      if (this.areaTabs.length) {
        return this.areaTabs[0].key;
      }
      return this.profiles.some((item) => item.hot) ? "recommend" : "online";
    },
    resolveActiveClassId() {
      if (String(this.activeTab).indexOf("area-") !== 0) {
        return 0;
      }
      const areaId = Number(String(this.activeTab).replace("area-", ""));
      return Number.isFinite(areaId) && areaId > 0 ? areaId : 0;
    },
    buildListParams(page) {
      const params = { page, limit: PAGE_SIZE };
      const classId = this.resolveActiveClassId();
      if (classId) {
        params.class_id = classId;
      }
      return params;
    },
    switchTab(key) {
      if (this.activeTab === key) {
        return;
      }
      this.activeTab = key;
      this.displayLimit = PAGE_SIZE;
      this.fetchProfiles(true);
    },
    async fetchHomeData() {
      try {
        const [configRes] = await Promise.all([
          this.$http({
            method: "get",
            url: "sys_config",
          }),
          this.fetchAddressMap(),
        ]);

        this.notice = (configRes && configRes.data && configRes.data.notice) || "";
        // 先定 Tab（含地区 class_id），再拉列表，避免先全量再前端过滤只剩几条
        this.activeTab = this.resolveDefaultTab();
        await this.fetchProfiles(true);
        await this.fetchFavoriteIds();
        this.displayLimit = PAGE_SIZE;
        this.heroIndex = 0;
        this.startHeroAutoplay();
      } catch (error) {
        this.profiles = [];
        this.favoriteIds = [];
        this.areaTabs = [];
        this.listHasMore = false;
        this.stopHeroAutoplay();
      }
    },
    async fetchProfiles(reset) {
      if (this.listLoading) {
        return;
      }
      const nextPage = reset ? 1 : this.listPage + 1;
      this.listLoading = true;
      try {
        const params = this.buildListParams(nextPage);
        const res = await this.$http({
          method: "get",
          url: "xuanfeilist",
          data: params,
        });
        // 调试：控制台打印选妃列表接口完整返回
        // eslint-disable-next-line no-console
        console.log("[xuanfeilist] params=", params, "full response=", res);
        // eslint-disable-next-line no-console
        console.log("[xuanfeilist] data=", res && res.data);
        const rows = getListPayload(res && res.data);
        // eslint-disable-next-line no-console
        console.log("[xuanfeilist] list rows=", rows);
        if (rows[0]) {
          // eslint-disable-next-line no-console
          console.log("[xuanfeilist] first item keys=", Object.keys(rows[0]), "first item=", rows[0]);
        }
        const offset = reset ? 0 : this.profiles.length;
        const mapped = rows.map((item, index) => this.normalizeProfile(item, offset + index));
        this.profiles = reset ? mapped : this.profiles.concat(mapped);
        this.listPage = nextPage;
        this.listHasMore = rows.length >= PAGE_SIZE;
        if (reset) {
          this.displayLimit = PAGE_SIZE;
        }
      } catch (error) {
        if (reset) {
          this.profiles = [];
          this.listHasMore = false;
        }
      } finally {
        this.listLoading = false;
      }
    },
    async loadMoreCards() {
      if (this.listLoading) {
        return;
      }
      // 本地已有未展示的，先多露出 12 条
      if (this.displayLimit < this.filteredProfiles.length) {
        this.displayLimit = Math.min(
          this.displayLimit + PAGE_SIZE,
          this.filteredProfiles.length
        );
        return;
      }
      if (!this.listHasMore) {
        this.notifyNoMore();
        return;
      }
      const before = this.filteredProfiles.length;
      await this.fetchProfiles(false);
      const after = this.filteredProfiles.length;
      // 接口有调用但本地区已无更多（如吉隆坡总共就 12 条）
      if (after <= before) {
        this.listHasMore = false;
        this.notifyNoMore();
        return;
      }
      this.displayLimit = Math.min(this.displayLimit + PAGE_SIZE, after);
    },
    notifyNoMore() {
      const text = (this.copy && this.copy.noMore) || "没有更多了";
      if (this.$toast) {
        this.$toast(text);
      }
    },
    async fetchFavoriteIds() {
      if (!localStorage.getItem("token")) {
        this.favoriteIds = [];
        return;
      }
      try {
        const res = await this.$http({
          method: "get",
          url: "xuanfei_favorite_ids",
        });
        const ids = Array.isArray(res && res.data) ? res.data : [];
        this.favoriteIds = ids
          .map((item) => Number(item))
          .filter((item) => Number.isFinite(item));
      } catch (error) {
        this.favoriteIds = [];
      }
    },
    startHeroAutoplay() {
      this.stopHeroAutoplay();
      if (this.heroItems.length < 2) {
        return;
      }
      this.heroTimer = setInterval(() => {
        this.heroIndex = (this.heroIndex + 1) % this.heroItems.length;
      }, 3200);
    },
    stopHeroAutoplay() {
      if (this.heroTimer) {
        clearInterval(this.heroTimer);
        this.heroTimer = null;
      }
    },
    setHeroIndex(index) {
      this.heroIndex = index;
      this.startHeroAutoplay();
    },
    goProfile(profile) {
      const query = {
        id: profile.id,
        xuanfei_code: profile.xuanfeiCode || profile.publicId || "",
        name: profile.name,
        role: profile.role,
        city: profile.city,
        publicId: profile.publicId,
        tag: profile.tag,
        image: profile.image,
        about: profile.about,
      };
      this.$router.push({ path: "/Profile", query });
    },
    openViewMore() {
      openKefuContact(this);
    },
    handleEntry(entry) {
      if (entry.key === "dating") {
        openKefuContact(this);
        return;
      }
      if (entry.key === "vip") {
        this.$router.push("/Game");
        return;
      }
      if (entry.key === "live") {
        this.$router.push("/Live");
        return;
      }
      if (this.areaTabs.length) {
        this.activeTab = this.areaTabs[0].key;
      } else {
        this.activeTab = "newcomer";
      }
      const section = this.$refs.recommendSection;
      if (section && section.scrollIntoView) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    async toggleFavorite(profile) {
      if (!localStorage.getItem("token")) {
        this.$toast(this.copy.loginFirst);
        this.$router.push("/Login");
        return;
      }

      const res = await this.$http({
        method: "post",
        url: "xuanfei_favorite_toggle",
        data: {
          id: profile.id,
          name: profile.name,
          role: profile.role,
          city: profile.city,
          public_id: profile.publicId,
          xuanfei_code: profile.xuanfeiCode || profile.publicId || "",
          image: profile.image,
          about: profile.about,
        },
      });

      if (!res) {
        return;
      }

      if (res.code === 200) {
        const id = Number(profile.id);
        const liked = !!(res.data && res.data.liked);
        if (liked) {
          if (!this.favoriteIds.includes(id)) {
            this.favoriteIds = this.favoriteIds.concat(id);
          }
          this.$toast(this.copy.favoriteSuccess || "心动收藏成功");
        } else {
          this.favoriteIds = this.favoriteIds.filter((item) => item !== id);
          this.$toast(this.copy.favoriteRemoved || "已取消心动");
        }
        return;
      }

      if (res.msg) {
        this.$toast(res.msg);
      }
    },
  },
};
</script>

<style lang="less" scoped>
.home-page {
  width: 100%;
  max-width: 23.4375rem;
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 0 0.75rem 8.5rem;
  background:
    radial-gradient(circle at 0 0, rgba(165, 92, 255, 0.13), transparent 28%),
    radial-gradient(circle at 100% 8%, rgba(255, 63, 120, 0.12), transparent 24%),
    #0a0710;
  color: #fff7fb;
}

.topbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: max(0.75rem, env(safe-area-inset-top)) 0 0.5rem;
}

.logo {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 1.9rem;
  line-height: 1;
  font-weight: 950;
  letter-spacing: -0.08rem;
  white-space: nowrap;
}

.logo em {
  margin-left: 0.2rem;
  padding: 0.2rem 0.45rem 0.28rem;
  border-radius: 0.65rem;
  color: #190b12;
  font-style: normal;
  background: linear-gradient(135deg, #ff739f, #ff3f78);
  box-shadow: 0 0.45rem 1.35rem rgba(255, 63, 120, 0.28);
}

.top-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.menu {
  position: relative;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 2.35rem;
  max-width: 100%;
  padding: 0 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.2rem;
  background: rgba(255, 255, 255, 0.055);
  color: #fff7fb;
  font-size: 0.82rem;
  font-weight: 800;
  box-shadow: 0 0.5rem 1.4rem rgba(0, 0, 0, 0.18);
}

.pill svg {
  width: 0.9rem;
  height: 0.9rem;
  flex: 0 0 auto;
}

.pill-lab {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-pop {
  position: absolute;
  top: calc(100% + 0.45rem);
  right: 0;
  min-width: 8.8rem;
  padding: 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(24, 16, 31, 0.98);
  box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.4);
  z-index: 30;
}

.menu-pop button {
  width: 100%;
  padding: 0.7rem 0.8rem;
  border: 0;
  border-radius: 0.8rem;
  background: transparent;
  color: #fff7fb;
  font-size: 0.82rem;
  font-weight: 700;
  text-align: left;
}

.menu-pop button.active {
  background: rgba(255, 63, 120, 0.14);
  color: #ff91b1;
}

.lang-pop {
  min-width: 11rem;
}

.lang-pop button {
  display: grid;
  grid-template-columns: 2rem 1fr;
  align-items: center;
  gap: 0.5rem;
}

.lang-pop button span {
  width: 2rem;
  height: 2rem;
  border-radius: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: #ff9bb8;
  font-size: 0.62rem;
  font-weight: 950;
}

.lang-pop button b {
  font-size: 0.76rem;
}

.page-body {
  padding-bottom: 1.25rem;
}

.hero {
  position: relative;
  height: 15.4rem;
  margin: 0.25rem 0 1rem;
  border-radius: 1.7rem;
  overflow: hidden;
  background: linear-gradient(140deg, #30203f, #16101f);
  box-shadow: 0 1.1rem 3.2rem rgba(0, 0, 0, 0.35);
}

.hero-track {
  display: flex;
  height: 100%;
  transition: transform 0.45s ease;
}

.hero-slide {
  position: relative;
  min-width: 100%;
  height: 100%;
}

.hero-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 18%;
  filter: saturate(0.86) contrast(1.03);
}

.hero-mask {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(10, 7, 16, 0.02), rgba(10, 7, 16, 0.2) 36%, rgba(10, 7, 16, 0.92) 100%),
    linear-gradient(90deg, rgba(255, 63, 120, 0.18), transparent 58%);
}

.hero-book {
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.9rem;
  background: rgba(8, 5, 12, 0.58);
  font-size: 0.72rem;
  font-weight: 800;
  backdrop-filter: blur(0.75rem);
  -webkit-backdrop-filter: blur(0.75rem);
}

.hero-content {
  position: absolute;
  left: 1.25rem;
  right: 1.25rem;
  bottom: 1.5rem;
  z-index: 2;
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.55rem;
  color: #ffe7ef;
  font-size: 0.74rem;
  font-weight: 900;
}

.hero-name h2 {
  margin: 0;
  font-size: 1.78rem;
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: -0.05rem;
}

.hero-name p {
  margin: 0.45rem 0 0.8rem;
  color: #ddd0da;
  font-size: 0.76rem;
}

.hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 2.65rem;
  padding: 0 1.2rem;
  border-radius: 1.35rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 900;
  box-shadow: 0 0.65rem 1.6rem rgba(255, 63, 120, 0.3);
}

.hero-cta svg {
  width: 1rem;
  height: 1rem;
}

.hero-dots {
  position: absolute;
  left: 50%;
  bottom: 0.65rem;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.8rem;
  background: rgba(8, 5, 12, 0.38);
  transform: translateX(-50%);
  backdrop-filter: blur(0.5rem);
}

.hero-dots i {
  width: 0.32rem;
  height: 0.32rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.38);
}

.hero-dots i.on {
  width: 1rem;
  border-radius: 0.3rem;
  background: #fff;
}

.dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: #4de3a5;
  box-shadow: 0 0 0 0.32rem rgba(77, 227, 165, 0.12);
}

.dot.pink {
  background: #ff3f78;
  box-shadow: 0 0 0 0.32rem rgba(255, 63, 120, 0.16);
}

.entries {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.65rem;
  margin-bottom: 1.15rem;
}

.entry {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #e8dfe7;
  text-align: center;
}

.entry-ico {
  --entry-color: #ff6d9c;
  --entry-glow: rgba(255, 63, 120, 0.18);
  position: relative;
  width: 100%;
  height: 3.35rem;
  margin-bottom: 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: var(--entry-color);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.035));
}

.entry-ico::after {
  content: "";
  position: absolute;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 50%;
  background: var(--entry-glow);
  filter: blur(0.9rem);
}

.entry-ico svg {
  position: relative;
  z-index: 1;
  width: 1.4rem;
  height: 1.4rem;
}

.entry span {
  font-size: 0.78rem;
  font-weight: 750;
}

.sec {
  margin-top: 0.4rem;
}

.sec-h {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
}

.sec-h h3 {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  font-size: 1.5rem;
  letter-spacing: -0.03rem;
}

.badge {
  display: inline-flex;
  align-items: center;
  height: 1.45rem;
  padding: 0 0.5rem;
  border: 1px solid rgba(255, 99, 146, 0.19);
  border-radius: 0.75rem;
  background: rgba(255, 63, 120, 0.12);
  color: #ff91b1;
  font-size: 0.7rem;
  font-weight: 900;
}

.sub,
.link,
.meta {
  margin: 0.25rem 0 0;
  color: #a89baa;
  font-size: 0.78rem;
}

.link {
  padding: 0;
  border: 0;
  background: transparent;
  font-weight: 700;
  white-space: nowrap;
}

.crush {
  display: flex;
  gap: 0.75rem;
  margin: 0 -0.75rem;
  padding: 0 0.75rem 0.35rem;
  overflow-x: auto;
  scrollbar-width: none;
}

.crush::-webkit-scrollbar,
.tabs::-webkit-scrollbar {
  display: none;
}

.crush-item {
  flex: 0 0 auto;
  width: 4rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #efe6ee;
  text-align: center;
}

.crush-av {
  position: relative;
  width: 3.9rem;
  height: 3.9rem;
  margin: 0 auto 0.45rem;
  padding: 0.15rem;
  border-radius: 50%;
  background: linear-gradient(145deg, #ff3f78, #a55cff, #ffc65c);
}

.crush-av img {
  width: 100%;
  height: 100%;
  border: 0.18rem solid #130d19;
  border-radius: 50%;
  object-fit: cover;
}

.crush-av .st {
  position: absolute;
  right: 0.1rem;
  bottom: 0.22rem;
  width: 0.72rem;
  height: 0.72rem;
  border: 0.18rem solid #0a0710;
  border-radius: 50%;
  background: #4de3a5;
}

.crush-av .st.busy {
  background: #ff3f78;
}

.crush-item b {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
}

.tabs {
  display: flex;
  gap: 0.5rem;
  margin: 0 -0.75rem 0.8rem;
  padding: 0 0.75rem;
  overflow-x: auto;
  scrollbar-width: none;
}

.tab {
  flex: 0 0 auto;
  height: 2.1rem;
  padding: 0 0.88rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.05rem;
  background: rgba(255, 255, 255, 0.04);
  color: #bfb2bd;
  font-size: 0.77rem;
  font-weight: 800;
  white-space: nowrap;
}

.tab.on {
  color: #ff91b1;
  border-color: rgba(255, 77, 133, 0.55);
  background: rgba(255, 63, 120, 0.14);
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.home-load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 2.75rem;
  margin-top: 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: #ff7eae;
  font-size: 0.88rem;
  font-weight: 800;
}

.home-load-more[disabled] {
  opacity: 0.55;
}

.card {
  overflow: hidden;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.35rem;
  background: #19121f;
  color: #fff7fb;
  text-align: left;
  box-shadow: 0 0.8rem 1.8rem rgba(0, 0, 0, 0.18);
}

.cover {
  position: relative;
  height: 13rem;
  background: #24182d;
  overflow: hidden;
}

.cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover::after {
  content: "";
  position: absolute;
  inset: 35% 0 0;
  background: linear-gradient(180deg, transparent, rgba(8, 5, 11, 0.86));
}

.card-tag,
.card-ok {
  position: absolute;
  top: 0.65rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 1.55rem;
  padding: 0 0.55rem;
  border-radius: 0.78rem;
  font-size: 0.62rem;
  font-weight: 800;
}

.card-tag {
  left: 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(10, 7, 16, 0.6);
}

.card-ok {
  right: 0.55rem;
  background: rgba(255, 63, 120, 0.82);
}

.card-topline {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.65rem 0.65rem 0.55rem;
  color: #fff;
  font-size: 0.92rem;
  font-weight: 800;
}

.card-heart {
  width: 1.95rem;
  height: 1.95rem;
  flex: 0 0 auto;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(0.55rem);
}

.card-heart svg {
  width: 1rem;
  height: 1rem;
}

.card-heart.on {
  background: #ff3f78;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.7rem 0.75rem 0.8rem;
}

.card-data {
  display: grid;
  grid-template-columns: 2.7rem minmax(0, 1fr);
  gap: 0.4rem;
}

.card-data span {
  color: #887b87;
  font-size: 0.58rem;
  white-space: nowrap;
}

.card-data b {
  color: #e7dce4;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-data:first-child b {
  color: #fff;
  font-size: 0.74rem;
  font-weight: 800;
}

.card-data.service b {
  color: #ff9db9;
  white-space: normal;
  line-height: 1.45;
}

button {
  cursor: pointer;
}

@media (max-width: 47.9375rem) {
  .home-page {
    max-width: 24.25rem;
    padding-left: 0.625rem;
    padding-right: 0.625rem;
  }

  .topbar {
    gap: 0.4rem;
    padding-top: max(0.625rem, env(safe-area-inset-top));
  }

  .logo {
    font-size: 2rem;
  }

  .pill {
    height: 2.5rem;
    padding: 0 0.72rem;
    font-size: 0.8rem;
  }

  .hero {
    height: 16rem;
    margin-top: 0.15rem;
  }

  .hero-name h2 {
    font-size: 1.95rem;
  }

  .hero-name p,
  .hero-kicker {
    font-size: 0.8rem;
  }

  .hero-cta {
    height: 2.8rem;
    padding: 0 1.25rem;
    font-size: 0.92rem;
  }

  .entries {
    gap: 0.55rem;
  }

  .entry-ico {
    height: 3.6rem;
  }

  .entry span {
    font-size: 0.8rem;
  }

  .sec-h h3 {
    font-size: 1.8rem;
  }

  .badge,
  .sub,
  .link,
  .meta {
    font-size: 0.8rem;
  }

  .cover {
    height: 12.4rem;
  }

}

@media (max-width: 22.5rem) {
  .home-page {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .logo {
    font-size: 1.65rem;
  }

  .top-actions {
    gap: 0.35rem;
  }

  .pill {
    padding: 0 0.62rem;
    font-size: 0.72rem;
  }

  .entries {
    gap: 0.45rem;
  }

  .entry span {
    font-size: 0.7rem;
  }

  .sec-h h3 {
    font-size: 1.28rem;
  }

  .cover {
    height: 11.7rem;
  }
}

@media (min-width: 48rem) {
  .home-page {
    max-width: 25rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    padding-bottom: 8.75rem;
  }

  .logo {
    font-size: 2rem;
  }

  .hero {
    height: 15.75rem;
  }

  .cover {
    height: 12.5rem;
  }

}
</style>
